import {get} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {Button, Input, PredefinedNotifierType, Select} from 'components';
import {ClientContext} from 'context';
import {useApiData, useClientSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, PreSessionFormField, PreSessionFormFieldInputType, PreSessionStep} from 'types';
import {apiUtils, sessionUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

type Props = {
    sessionId: string;
    participantId: string;
    step: PreSessionStep;
    skipButton?: React.ReactNode;
    onBusyChange?: (busy: boolean) => void;
};

// Pre-fill each field from the client's existing userInfo. `field.label` is the userInfo path
// (e.g. "firstName", "contacts.email") — the same path the backend mirrors submitted values into.
const initialValues = (fields: PreSessionFormField[], userInfo: unknown): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const field of fields) {
        const existing = field.label ? get(userInfo, field.label) : undefined;
        result[field.id] = existing === null || existing === undefined ? '' : String(existing);
    }
    return result;
};

export const FormStep: React.FC<Props> = ({sessionId, participantId, step, skipButton, onBusyChange}) => {
    const {t} = useTranslation();
    const {session} = useClientSession();
    const fields = useMemo(() => step.form?.fields ?? [], [step]);
    const userInfo = useMemo(() => sessionUtils.getClientParticipant(session)?.data?.userInfo, [session]);
    const {preSessionFormDrafts} = useContext(ClientContext);
    const draftKey = useMemo(() => `${sessionId}:${step.id}`, [sessionId, step.id]);
    const [values, setValues] = useState<Record<string, string>>(() => {
        const savedValues = initialValues(fields, userInfo);
        const draft = preSessionFormDrafts?.current[draftKey];
        if (draft) {
            for (const field of fields) {
                if (draft.initialValues[field.id] === savedValues[field.id] && draft.values[field.id] !== undefined) {
                    savedValues[field.id] = draft.values[field.id];
                }
            }
        }
        return savedValues;
    });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const handleError = useCallback((err: ApiError) => {
        // Map the backend's per-field validation codes to localized messages keyed by field id.
        const next: Record<string, string> = {};
        for (const field of fields) {
            const message = apiUtils.getFieldValidationErrorMessage(err, t, field.id);
            if (message) {
                next[field.id] = message;
            }
        }
        setFieldErrors(next);
    }, [fields, t]);

    const handleSuccess = useCallback(() => {
        if (preSessionFormDrafts) {
            delete preSessionFormDrafts.current[draftKey];
        }
    }, [preSessionFormDrafts, draftKey]);

    const {send: submitForm, loading} = useApiData(clientActionsApi.submitPreSessionForm, {
        notificationType: PredefinedNotifierType.SaveError,
        onError: handleError,
        onSuccess: handleSuccess,
    });

    useEffect(() => {
        onBusyChange?.(loading);
    }, [loading, onBusyChange]);

    const handleFieldChange = useCallback((fieldId: string, value: string) => {
        const nextValues = {...values, [fieldId]: value};
        setValues(nextValues);
        if (preSessionFormDrafts) {
            preSessionFormDrafts.current[draftKey] = {values: nextValues, initialValues: initialValues(fields, userInfo)};
        }
        // Clear a field's error as soon as the client edits it.
        setFieldErrors((prev) => (prev[fieldId] ? {...prev, [fieldId]: ''} : prev));
    }, [values, preSessionFormDrafts, draftKey, fields, userInfo]);

    const isValid = useMemo(() => {
        for (const field of fields) {
            if (field.required && !values[field.id]?.trim()) {
                return false;
            }
        }
        return true;
    }, [fields, values]);

    const handleSubmit = useCallback(() => {
        if (!isValid) {
            return;
        }
        submitForm({sessionId, participantId}, {stepId: step.id, values});
    }, [isValid, submitForm, sessionId, participantId, step, values]);

    const resolveLabel = useCallback(
        (field: PreSessionFormField) => field.title?.trim() || field.label?.trim() || step.name?.trim() || '',
        [step.name],
    );

    return (
        <div className='tw-flex tw-flex-col tw-gap-4'>
            {fields.map((field) => {
                if (field.inputType === PreSessionFormFieldInputType.Select) {
                    return (
                        <Select
                            key={field.id}
                            name={field.id}
                            placeholder={resolveLabel(field)}
                            required={field.required}
                            valueAs='id'
                            value={values[field.id]}
                            hasError={!!fieldErrors[field.id]}
                            errorMessage={fieldErrors[field.id]}
                            options={(field.options ?? []).map((opt) => ({id: opt, name: opt}))}
                            onChange={(value) => handleFieldChange(field.id, String(value ?? ''))}
                        />
                    );
                }
                return (
                    <Input
                        key={field.id}
                        name={field.id}
                        label={resolveLabel(field)}
                        required={field.required}
                        value={values[field.id]}
                        maxLength={field.maxLength}
                        hasError={!!fieldErrors[field.id]}
                        errorMessage={fieldErrors[field.id]}
                        onChange={(value) => handleFieldChange(field.id, value)}
                    />
                );
            })}
            <div className='tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                <div className='tw-flex-1'>
                    <Button
                        color='primary'
                        disabled={!isValid || loading}
                        loading={loading}
                        onClick={handleSubmit}
                    >
                        {t(T_KEYS.SUBMIT)}
                    </Button>
                </div>
                {skipButton}
            </div>
        </div>
    );
};
